import React from 'react';
import { createQueryParams, readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { useNavigate } from 'react-router-dom';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  noop,
  OnChangeFn,
  PaginationState,
  RowData,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import {
  INaviTable,
  NaviTableSortingDirections,
  PaginationOptions,
  SortingOptions,
  UseNaviTableProps
} from '../interfaces';

const useNaviTable = <TData extends RowData>(
  props: INaviTable<TData>
): UseNaviTableProps<TData> => {
  const {
    defaultColumnDef,
    columns,
    headerTopCoordinate = 0,
    tableName = 'NaviTable',
    paginationType = PaginationOptions.SERVER_SIDE,
    sortingType = SortingOptions.SERVER_SIDE,
    defaultPaginationState,
    defaultSortingState,
    sizeColumnsToFit = true,
    onPaginationChange,
    onSortingChange,
    doNotNavigate = false,
    ...restProps
  } = props;
  const [isScrolled, setIsScrolled] = React.useState(false);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const headerLineRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLDivElement>(null);
  const rerender = React.useReducer(() => ({}), {})[1];

  const queryParams = readQueryParams();
  const tableQueryParams = queryParams[tableName] || {};
  const navigate = useNavigate();

  const updatedNavigation = (url: string) => {
    doNotNavigate ? noop : navigate(url);
  };

  const { leftPinnedColumns, rightPinnedColumns, totalWidth, leftPinnedWidth } = React.useMemo(
    () =>
      columns.reduce(
        (acc, column) => {
          acc.totalWidth += column.size || 0;
          if (!column.accessorKey) return acc;
          if (column.pin === 'left') {
            acc.leftPinnedColumns.push(column.accessorKey);
            acc.leftPinnedWidth += column.size || 0;
          } else if (column.pin === 'right') {
            acc.rightPinnedColumns.push(column.accessorKey);
          }
          return acc;
        },
        {
          leftPinnedColumns: [] as string[],
          rightPinnedColumns: [] as string[],
          totalWidth: 0,
          leftPinnedWidth: 0
        }
      ),
    [columns]
  );

  const paginationParams = {
    pageIndex: Number(tableQueryParams.pageIndex) || defaultPaginationState?.pageIndex || 0,
    pageSize: Number(tableQueryParams.pageSize) || defaultPaginationState?.pageSize || 50
  };

  const sortingParams: SortingState | undefined = tableQueryParams.sortBy
    ? [
        {
          id: tableQueryParams.sortBy,
          desc: tableQueryParams.sortDirection === NaviTableSortingDirections.DESC
        }
      ]
    : [];

  const handleSortingChange = (sorting: (old: SortingState) => SortingState) => {
    const updatedSortingParams = sorting(sortingParams)?.[0];
    if (
      updatedSortingParams?.id === sortingParams[0]?.id &&
      updatedSortingParams?.desc === sortingParams[0]?.desc
    ) {
      return;
    }
    onSortingChange?.(updatedSortingParams);
    let updatedTableParams = { ...queryParams };
    if (updatedSortingParams) {
      updatedTableParams = {
        ...queryParams,
        [tableName]: {
          ...tableQueryParams,
          sortBy: updatedSortingParams.id.toString(),
          sortDirection: updatedSortingParams.desc
            ? NaviTableSortingDirections.DESC
            : NaviTableSortingDirections.ASC,
          pageIndex: '0'
        }
      };
    } else {
      delete updatedTableParams[tableName].sortBy;
      delete updatedTableParams[tableName].sortDirection;
      updatedTableParams[tableName].pageIndex = '0';
    }
    const url = createQueryParams(updatedTableParams);
    updatedNavigation(url);
  };

  const handlePaginationChange = (pagination: (old: PaginationState) => PaginationState) => {
    const updatedPaginationParams = pagination(paginationParams);
    if (
      paginationParams.pageIndex === updatedPaginationParams.pageIndex &&
      paginationParams.pageSize === updatedPaginationParams.pageSize
    ) {
      return;
    }
    onPaginationChange?.(updatedPaginationParams);
    if (!updatedPaginationParams) {
      return;
    }
    const updatedTableParams = {
      ...queryParams,
      [tableName]: {
        ...tableQueryParams,
        pageSize: updatedPaginationParams.pageSize.toString(),
        pageIndex: updatedPaginationParams.pageIndex.toString()
      }
    };
    const url = createQueryParams(updatedTableParams);
    updatedNavigation(url);
  };

  const table = useReactTable({
    ...restProps,
    columns,
    defaultColumn: {
      enableSorting: false,
      ...defaultColumnDef
    },
    initialState: {
      columnPinning: {
        left: leftPinnedColumns,
        right: rightPinnedColumns
      }
    },
    state: {
      sorting: sortingParams,
      pagination: paginationParams
    },
    manualSorting: sortingType === SortingOptions.SERVER_SIDE,
    manualPagination: paginationType === PaginationOptions.SERVER_SIDE,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: handleSortingChange as OnChangeFn<SortingState>,
    onPaginationChange: handlePaginationChange as OnChangeFn<PaginationState>,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel()
  });

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsScrolled(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `-${headerTopCoordinate}px 0px 0px 0px` // Adjust rootMargin to control when the shadow appears
      }
    );

    if (headerLineRef.current) {
      observer.observe(headerLineRef.current);
    }

    return () => {
      if (headerLineRef.current) {
        observer.unobserve(headerLineRef.current);
      }
    };
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (headerRef.current && !sizeColumnsToFit) {
      const { scrollLeft } = e.currentTarget;
      headerRef.current.scrollLeft = scrollLeft;
    }
  };

  return {
    table,
    totalWidth,
    tableRef,
    headerRef,
    headerLineRef,
    isScrolled,
    rerender,
    handleScroll,
    leftPinnedWidth
  };
};

export default useNaviTable;
